//텍스트 형식 변환 함수 처리

import dayjs from 'dayjs'
import {
  ChatStatus,
  DashboardCuratedScoutRequest,
  DashboardScoutsRecommendedTabType,
  DashboardTempScoutProposal,
  Location,
  NumberOrNull,
  OtherActivityHistory,
  Range,
  WithIdName,
  WithIdNameParentId,
} from 'types/common'
import { getDiffFromPast } from 'utils/formatters'
import { escapeRegExp } from 'lodash-es'
import {
  DEFAULT_MAX_DEV_COUNT,
  DEFAULT_MAX_MEMBER_COUNT,
  IS_BROWSER,
} from 'infra/constants'

const today = new Date()

export const displaySalaryNegotiableWithStockOption = (
  isSalaryNegotiableWithStockOption: boolean,
) =>
  isSalaryNegotiableWithStockOption
    ? '스톡옵션으로 연봉 협상 가능'
    : '스톡옵션 원하지만 연봉 수준은 유지'

export const getAge = (birthYear: number) => {
  const nowYear = new Date().getFullYear()
  return nowYear - birthYear + 1
}

export const displayExperienceDesc = (exp: number) => {
  if (exp === 0) return '1년 미만'
  if (exp === 10) return '10년 이상'
  return `${exp}년`
}

export const getApplicationChatStatus = (statusId?: number) => {
  if (statusId === 4) return ChatStatus.RESUME_ACCEPTED
  if (statusId === 2) return ChatStatus.ACCEPTED
  if (statusId === 3) return ChatStatus.REJECTED
  return ChatStatus.CHATTING
}
export const getProposalChatStatus = (
  proposal: {
    isRejected?: boolean
    isCanceled?: boolean
    hasDoneMeet?: boolean
    hasConfirmedMeet?: boolean
  },
  isStartup?: boolean,
) => {
  // 채팅 거절 혹은 기업이 채팅 종료 -> "채팅 종료"
  if (proposal.isRejected || proposal.isCanceled) return ChatStatus.ENDED
  // 제안 후 응답 전 초기값 -> "미응답"
  if (proposal.isRejected === null)
    return isStartup ? ChatStatus.NOT_ACCEPTED : ChatStatus.NOT_RESPONSED

  // "커피챗 완료"
  if (proposal.hasDoneMeet) return ChatStatus.MEET_DONE
  // "커피챗 예정"
  if (proposal.hasConfirmedMeet) return ChatStatus.MEET_CONFIRMED
  // 위 조건에 모두 해당하지 않을 때 -> "채팅 진행중"
  return ChatStatus.CHATTING
}

export function convertHyphenPhoneToNumber(hyphenPhone: string) {
  // 하이픈을 제거한 번호 (숫자가 아닌 문자가 입력되는 것도 방지)
  return hyphenPhone.replace(/[^0-9]/g, '')
}

export function convertNumberToHyphenPhone(value: string) {
  // 휴대폰 자동하이픈 생성
  if (value.length === 10) {
    return value
      .replace(/[^0-9]/g, '')
      .replace(/^(\d{0,2})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
      .replace(/(-{1,2})$/g, '')
  }
  return value
    .replace(/[^0-9]/g, '')
    .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
    .replace(/(-{1,2})$/g, '')
}

export const formatEducationPeriod = (
  admissionYear: string | null,
  graduationYear: string | null,
) => {
  if (!admissionYear) return ''
  return `${admissionYear}${graduationYear ? ` - ${graduationYear}` : ''}`
}

export const getHashtagText = (text: string) => {
  return `#${text.replaceAll(' ', '_')}`
}

export const lastMessageDateFormat = (date: string) => {
  const now = new Date()
  const curDate = new Date(date)
  const diffTimezone = now.getTime() - curDate.getTime()
  const dayjsDate = dayjs(curDate)
  const miMill = 1000 * 60
  const hrMill = miMill * 60
  const dyMill = hrMill * 24
  const yrMill = dyMill * 365
  if (diffTimezone < miMill) return '방금'
  if (diffTimezone < dyMill) return dayjsDate.format('A hh:mm')
  if (diffTimezone > yrMill || now.getFullYear() !== curDate.getFullYear())
    return dayjsDate.format('YY.MM.DD')
  return dayjs(curDate).format('M월 D일')
}

export const getProposalMessage = (proposal: DashboardTempScoutProposal) =>
  `${proposal.introPerson}\n\n${proposal.proposeReason}\n\n${proposal.appealPoints}\n\n${proposal.outro}`

export const getDevCountCondition = (min: number, max: number) => {
  let value = ''
  if (min === 0) {
    if (max === DEFAULT_MAX_DEV_COUNT) return ''
    value = `개발팀 ${max}명 이하`
  } else {
    value =
      max === DEFAULT_MAX_DEV_COUNT
        ? `개발팀 ${min}명 이상`
        : `개발팀 ${min}~${max}명`
  }
  return value
}

export const getMemberCountCondition = (min: number, max: number) => {
  let value = ''
  if (min === 0) {
    if (max === DEFAULT_MAX_MEMBER_COUNT) return ''
    value = `전체 ${max}명 이하`
  } else {
    value =
      max === DEFAULT_MAX_MEMBER_COUNT
        ? `전체 ${min}명 이상`
        : `전체 ${min}~${max}명`
  }
  return value
}

export const getSalaryCondition = (min: number, max: number) => {
  if (min === 0 && max === 10000) return `연봉 무관`
  if (min === 0 && max !== 10000) return `연봉 ${max}만원 이하`
  if (min !== 0 && max === 10000)
    return `연봉 ${min === 10000 ? '1억' : `${min}만`}원 ${
      !IS_BROWSER // pdf 다운로드시 서버에서 window를 인식하지 못하여 에러가 발생합니다.
        ? '수준'
        : !window.location.pathname.includes('dashboard')
        ? '이상'
        : '수준'
    }`
  if (min !== 0 && max !== 10000) return `연봉 ${min}~${max}만원`
  return ''
}

export const getJobInterestLevel = (jobInterestLevel: number | null) => {
  if (jobInterestLevel === 1) return '인턴만 희망'
  if (jobInterestLevel === 2) return '정규직 전환 관심'
  if (jobInterestLevel === 3) return '꼭 정규직 전환 희망'
  if (jobInterestLevel === 11) return '팀장급 희망'
  if (jobInterestLevel === 12) return 'C-Level 희망'
  return ''
}

export const displayFilterValue = (
  selectedData: number[],
  data: WithIdNameParentId[],
) => {
  return selectedData.length > 0 && data.length > 0
    ? selectedData.length ===
      data.filter(
        (item) =>
          item.parentId ===
          data.find((item) => item.id === selectedData[0])?.parentId,
      ).length
      ? data.find(
          (item) =>
            item.id ===
            data.find((item) => item.id === selectedData[0])?.parentId,
        )?.name + ' 전체'
      : data.filter((item) => selectedData.includes(item.id))[0].name
    : ''
}

export const getWorkLevelExperienceFormat = (
  workLevelId: NumberOrNull,
  experience: number,
) => {
  if (workLevelId === 1) return '인턴'
  if (workLevelId === 2) return '신입'
  if (experience === 0) return '1년 미만'
  if (experience === 10) return '10년 이상'
  return experience + '년'
}

export const getFilterDisplayName = (v: WithIdName[]) => {
  if (v.length > 1) return `${v[0].name} 외 ${v.length - 1}`
  return v.length > 0 ? v[0].name : ''
}

export const displayRange = ({
  value,
  min,
  max,
  unit = '',
}: {
  value: Range
  min: number
  max: number
  unit?: string
}) => {
  if (value.min === min && value.max === max) return ''
  if (value.min === min && value.max !== max) return `${value.max}${unit} 이하`
  if (value.max === max && value.min !== min) return `${value.min}${unit} 이상`
  return `${value.min}~${value.max}${unit}`
}

export const displayPositionName = (
  name: string,
  careerType: string,
  experienceRange: Range | null,
) => {
  return `${name} · ${careerType}${
    careerType === '경력' && experienceRange
      ? ` ${formatExperienceRange(experienceRange.min, experienceRange.max)}`
      : ' '
  }`
}

export const formatExperienceRange = (min: number, max: number) => {
  return min === 0 && max === 10
    ? '전체'
    : min === max
    ? max + '년'
    : min === 0 && max === 0
    ? '1년 미만'
    : min === 10 && max === 10
    ? '10년 이상'
    : max === 10 && min !== 0
    ? min + '년 이상'
    : min === 0 && max !== 10
    ? max + '년 이하'
    : `${min}~${max}년`
}

export const displayOtherActivityPeriod = (
  otherActivity: OtherActivityHistory,
) => {
  return `${dayjs(otherActivity.started).format('YYYY.MM')}${
    otherActivity.ended || otherActivity.isOnGoing
      ? `- ${
          otherActivity.isOnGoing
            ? '현재'
            : dayjs(otherActivity.ended).format('YYYY.MM')
        } ‧ ${calcPeriod(
          dayjs(otherActivity.started + '-01 00:00').format('YYYY-MM-DD HH:mm'),
          dayjs(
            otherActivity.isOnGoing
              ? new Date()
              : otherActivity.ended + '-01 00:00',
          )
            .add(1, 'M')
            .format('YYYY-MM-DD HH:mm'),
        )}`
      : ''
  }`
}

export const displayCareerPeriod = ({
  started,
  ended,
  isWorking,
}: {
  started: string | null
  ended: string | null
  isWorking: boolean
}) => {
  return `${dayjs(started).format('YYYY.MM')} - ${
    isWorking ? '현재' : dayjs(ended).format('YYYY.MM')
  } ‧ ${calcPeriod(
    dayjs(started + '-01 00:00').format('YYYY-MM-DD HH:mm'),
    dayjs(isWorking ? new Date() : ended + '-01 00:00')
      .add(1, 'M')
      .format('YYYY-MM-DD HH:mm'),
  )}`
}

export const calcPeriod = (startedAt: string, endedAt: string) => {
  const start = new Date(startedAt)
  const end = new Date(endedAt)
  const year = dayjs(end).diff(start, 'year')
  const month = dayjs(end).diff(start, 'month')
  const diffMonth = month - year * 12
  if (year === 0) return `${month === 0 ? 1 : month}개월`
  return `${year}년${diffMonth !== 0 ? ` ${diffMonth}개월` : ''}`
}

export const calcDateBefore = (before: string) => {
  const date = new Date(before)
  let diff = (new Date().getTime() - date.getTime()) / 1000 // 초 단위
  if (diff < 60) return `${Math.floor(diff)}초 전`
  diff = diff / 60 // 분 단위
  if (diff < 60) return `${Math.floor(diff)}분 전`
  diff = diff / 60 // 시간 단위
  if (diff < 24) return `${Math.floor(diff)}시간 전`
  diff = diff / 24 // 일 단위
  if (diff < 7) return `${Math.floor(diff)}일 전`
  diff = diff / 7 // 주 단위
  return `${Math.floor(diff)}주 전`
}

export const calcPublishedDate = (value: string) => {
  const date = new Date(value)
  const diffDay = dayjs(today).diff(date, 'day')
  if (diffDay > 364) return `${Math.floor(diffDay / 365)}년 전`
  if (diffDay > 29) return `${Math.floor(diffDay / 30)}달 전`
  if (diffDay > 6) return `${Math.floor(diffDay / 7)}주 전`
  if (diffDay === 0) return '오늘'
  if (diffDay <= 6) return `${diffDay}일 전`
  return ''
}

export const getDueDateMessage = (dueDate?: string) => {
  if (!dueDate) return '7일 후 자동으로 채팅이 거절돼요'
  const remainingDays = Math.ceil(
    (new Date(dueDate).getTime() - today.getTime()) / (1000 * 3600 * 24),
  )
  return remainingDays > 0
    ? `${remainingDays}일 후 자동으로 채팅이 거절돼요`
    : '오늘이 지나면 자동으로 채팅이 거절돼요'
}

export const calcTodayFromDate = (date: string) => {
  const diff = getDiffFromPast(date)
  return diff >= 0 ? `D+${diff}` : '날짜가 잘못되었습니다.'
}

export const addHighlight = (text: string, keyword?: string) => {
  const _keyword = keyword || text
  // 이미 해당 키워드가 하이라이팅되어 있으면 건너뛰기
  const highlightedKeyword = `<b class=highlighted>${_keyword}</b>`
  if (text.includes(highlightedKeyword)) return text

  return text.replaceAll(
    new RegExp(escapeRegExp(_keyword), 'gi'),
    `<b class=highlighted>${_keyword}</b>`,
  )
}

export const getLocationName = (
  selectedLocation: Location,
  locations: Location[],
) => {
  return `${
    selectedLocation.parentId
      ? locations.find((item) => item.id === selectedLocation.parentId)?.name +
        ' '
      : ''
  }${selectedLocation.name}`
}

export const displayNumberWithCommas = (number: number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const shortenItems = (arr: string[]) => {
  return arr.length > 3
    ? `${arr[0]}, ${arr[1]} 외 ${arr.length - 2}`
    : arr.join(', ')
}

const TEXT_BLUR_PLACEHOLDER =
  '로그인하시면 자세한 내용을 바로 확인하실 수 있어요. '

export const blurText = (text: string) => {
  return TEXT_BLUR_PLACEHOLDER.repeat(
    Math.ceil(text.length / TEXT_BLUR_PLACEHOLDER.length),
  ).slice(0, text.length)
}

export const makeRandomSpaces = (text: string) => {
  const spaceCount = (text.match(/ /g) || []).length
  const chars = text.replace(/ /g, '').split('')
  if (spaceCount === 0) return chars.join('')

  // 삽입 위치 선정 (0~chars.length 사이에서 중복 없이)
  const positions = Array.from({ length: chars.length + 1 }, (_, i) => i)
    .sort(() => Math.random() - 0.5)
    .slice(0, spaceCount)
    .sort((a, b) => a - b)

  positions.forEach((pos, idx) => chars.splice(pos + idx, 0, ' '))
  return chars.join('')
}

export const getCuratedStatusInfo = (
  tabId: DashboardScoutsRecommendedTabType,
  item: DashboardCuratedScoutRequest,
) => {
  if (tabId === 'newCurated') {
    return { name: '추천', date: item.curatedAt || '' }
  } else if (tabId === 'waiting') {
    return {
      name:
        item.proposedAt && item.brokingRequestedAt
          ? new Date(item.proposedAt) > new Date(item.brokingRequestedAt)
            ? '제안 보냄'
            : '연결 요청'
          : item.proposedAt
          ? '제안 보냄'
          : '연결 요청',
      date:
        item.proposedAt && item.brokingRequestedAt // 일시가 둘다 있으면 최신 일시로 반영
          ? new Date(item.proposedAt) > new Date(item.brokingRequestedAt)
            ? item.proposedAt
            : item.brokingRequestedAt
          : item.proposedAt || item.brokingRequestedAt || '',
    }
  } else if (tabId === 'rejected') {
    return {
      name: '거절',
      date:
        item.brokingRequestStatus === 'REJECTED' && item.rejectedAt // 연결요청도 거절되고 스카우트 제안도 거절 시
          ? new Date(item.brokingRequestStatusUpdatedAt || '') >
            new Date(item.rejectedAt) // 더 최신에 발생한 것 표시
            ? item.brokingRequestStatusUpdatedAt || ''
            : item.rejectedAt
          : item.brokingRequestStatus === 'REJECTED'
          ? item.brokingRequestStatusUpdatedAt || ''
          : item.rejectedAt || '',
    }
  } else if (tabId === 'canceled') {
    return {
      name:
        item.brokenAt || item.canceledAt
          ? '불합격'
          : item.uninterestedAt
          ? '관심 없음'
          : '',
      date: item.brokenAt || item.canceledAt || item.uninterestedAt || '',
    }
  } else {
    return {
      name:
        item.brokingRequestStatus === 'ACCEPTED' && item.userAcceptedAt
          ? new Date(item.brokingRequestStatusUpdatedAt || '') >
            new Date(item.userAcceptedAt)
            ? '연결 수락'
            : '제안 수락'
          : item.brokingRequestStatus === 'ACCEPTED'
          ? '연결 수락'
          : '제안 수락',
      date:
        item.brokingRequestStatus === 'ACCEPTED' && item.userAcceptedAt
          ? new Date(item.brokingRequestStatusUpdatedAt || '') >
            new Date(item.userAcceptedAt)
            ? item.brokingRequestStatusUpdatedAt || ''
            : item.userAcceptedAt
          : item.brokingRequestStatus === 'ACCEPTED'
          ? item.brokingRequestStatusUpdatedAt || ''
          : item.userAcceptedAt || '',
    }
  }
}

// dangerousSetInnerHTML에 넣을 텍스트에 HTML 태그가 아닌 "<" 사용하기 위한 처리
export const escapeHtml = (str: string) => {
  return str.replace(/<(?!\/?[A-Za-z][^<>]*>)/g, '&lt;')
}
